import { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import UnifiedAuthModal from './UnifiedAuthModal';
import { deleteSecret } from './Api';
import { useHttpClient } from './HttpClientContext';
import { jsx as _jsx } from "react/jsx-runtime";
export function useUnifiedAuthModal(options, callbacks) {
  const {
    portalId,
    authenticatorId,
    appName,
    oAuthScope,
    appId,
    mcp,
    shouldDeleteOnFailure,
    introScreen,
    successScreen,
    usePortal = true
  } = options;
  const {
    onComplete,
    onCancel
  } = callbacks;
  const http = useHttpClient();
  const [isOpen, setIsOpen] = useState(false);
  const portalContainerRef = useRef(null);
  useEffect(() => {
    if (!usePortal) {
      return;
    }
    const container = document.createElement('div');
    container.setAttribute('data-unified-auth-modal-portal', 'true');
    document.body.appendChild(container);
    portalContainerRef.current = container;
    return () => {
      if (portalContainerRef.current && document.body.contains(portalContainerRef.current)) {
        document.body.removeChild(portalContainerRef.current);
        portalContainerRef.current = null;
      }
    };
  }, [usePortal]);
  const openModal = useCallback(() => {
    setIsOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);
  const handleComplete = useCallback(secretId => {
    let onCompleteSucceeded = false;
    try {
      onComplete(secretId);
      onCompleteSucceeded = true;
    } catch (err) {
      const shouldCleanup = shouldDeleteOnFailure ? shouldDeleteOnFailure(secretId) : true;
      if (shouldCleanup) {
        void deleteSecret(http, portalId, authenticatorId, secretId);
      }
      throw err;
    } finally {
      if (onCompleteSucceeded && !successScreen) {
        closeModal();
      }
    }
  }, [http, onComplete, shouldDeleteOnFailure, successScreen, closeModal, portalId, authenticatorId]);
  const handleCancel = useCallback(() => {
    closeModal();
    onCancel === null || onCancel === void 0 || onCancel();
  }, [closeModal, onCancel]);
  const modalElement = isOpen ? /*#__PURE__*/_jsx(UnifiedAuthModal, {
    portalId: portalId,
    authenticatorId: authenticatorId,
    appName: appName,
    oAuthScope: oAuthScope,
    appId: appId,
    mcp: mcp,
    onComplete: handleComplete,
    onCancel: handleCancel,
    introScreen: introScreen,
    successScreen: successScreen
  }) : null;
  const Modal = usePortal && isOpen && portalContainerRef.current ? /*#__PURE__*/createPortal(modalElement, portalContainerRef.current) : modalElement;
  return {
    openModal,
    closeModal,
    isOpen,
    Modal
  };
}
export default useUnifiedAuthModal;